import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { PokeTeam } from "@/types";
import { isXsScreen } from "@/utils/screen-size-check";
import cn from "classnames";

type TeamPokemon = PokeTeam["pokemons"][number];

interface ArenaTeamSelectCardProps {
  team: PokeTeam;
  onClick?: (team: PokeTeam) => void;
}

function getOrderedSlots(team: PokeTeam): (TeamPokemon | null)[] {
  return Array.from({ length: 8 }).map((_, index) => {
    const pokeId = team.order?.[index as keyof typeof team.order];
    if (!pokeId) return null;
    const poke = team.pokemons.find((p) => p.id === pokeId);
    return poke ?? null;
  });
}

/**
 * Arena team selection card - bold, compact, game-style design.
 */
export function ArenaTeamSelectCard({
  team,
  onClick,
}: ArenaTeamSelectCardProps) {
  const slots = getOrderedSlots(team);
  const filledCount = slots.filter((s) => s !== null).length;

  return (
    <div
      role={onClick ? "button" : undefined}
      onClick={onClick ? () => onClick(team) : undefined}
      className={cn(
        "relative overflow-hidden group transition-all duration-200 rounded-xl border-4 border-gray-900 shadow-cartoon",
        onClick && "cursor-pointer hover:scale-[1.02] active:scale-[0.98]",
      )}
    >
      {/* Main card with gradient background */}
      <div className="relative bg-gradient-to-br from-pk-night-2 to-pk-night-1 p-3">
        {/* Animated shine sweep */}
        <div
          className={cn(
            "absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full transition-transform duration-700 ease-in-out pointer-events-none",
            onClick && "group-hover:translate-x-full",
          )}
        />

        {/* Texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage: "url('/assets/app-ui/textre.png')",
            backgroundSize: "cover",
            backgroundRepeat: "repeat",
          }}
        />

        {/* Skewed accent bar */}
        <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-gradient-to-l from-pk-garnet-1/20 to-transparent skew-x-[-20deg] translate-x-8 pointer-events-none" />

        <div className="relative z-10 flex items-center justify-between mb-3">
          {/* Team name - big and bold like play menu */}
          <div className="flex-1 min-w-0">
            <h3 className="text-white text-xl xs:text-2xl font-black tracking-wider italic drop-shadow-[2px_2px_0_rgba(0,0,0,0.8)] truncate uppercase">
              {team.name}
            </h3>
          </div>

          {/* Count badge */}
          <div className="flex items-center gap-1 ml-2">
            <div
              className={cn(
                "text-xs font-bold px-2 py-0.5 rounded-sm uppercase tracking-wider shadow-cartoon-sm",
                filledCount >= 8
                  ? "bg-pk-green-2 text-white"
                  : "bg-pk-gray-3 text-pk-gray-5",
              )}
            >
              {filledCount}/8
            </div>
          </div>
        </div>

        {/* Pokemon grid - compact and visual */}
        <div className="relative z-10 grid grid-cols-4 gap-1">
          {slots.map((pokemon, index) => (
            <div
              key={pokemon ? pokemon.id : `empty-${index}`}
              className={cn(
                "relative flex items-center justify-center h-16 rounded-sm border-2 transition-all",
                pokemon
                  ? "bg-pk-purple-2/50 border-pk-purple-3 group-hover:bg-pk-purple-2/70"
                  : "bg-pk-gray-1/30 border-pk-gray-2",
              )}
            >
              {pokemon ? (
                <>
                  {/* Sprite */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <PokemonSprite
                      pokemon={pokemon}
                      className="w-14 h-14"
                      style={{ scale: isXsScreen() ? 1.3 : 1.6 }}
                      ignoreScale={true}
                    />
                  </div>

                  {/* Pokemon name at bottom */}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full px-0.5">
                    <StrokedText
                      fontSize={9}
                      strokeWidth={6}
                      className="text-center truncate"
                    >
                      {pokemon.base_pokemon_name}
                    </StrokedText>
                  </div>
                </>
              ) : (
                <span className="text-pk-gray-4 text-xl opacity-30">—</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
